/**
 * V4WorkflowCreateDialog — Phase 5c-2 + 5c-3.
 *
 * Small Modal used twice:
 *   - mode='new'      → fresh workflow seed (Start → End sentinel)
 *   - mode='save-as'  → clone of the currently-loaded workflow definition
 *
 * The host owns the actual POST + navigation; this component just
 * collects name + description and disposes onConfirm({ name, description }).
 *
 * Modal (not Slideout) because this is a small focused confirm-with-input
 * surface — single decision, two fields, irreversible-ish (creates a new
 * row server-side). Same pattern as kill-switch confirm, delete-with-reason,
 * etc. Per V2 §5.5/§3.13.
 */

import React, { useEffect, useState } from 'react';
import { Modal, Button } from '../../components/v2';
import { Plus, Copy as CopyIcon } from 'lucide-react';

export default function V4WorkflowCreateDialog({
  isOpen,
  mode,                    /* 'new' | 'save-as' */
  defaultName,             /* pre-filled name (Save As → "<current> copy") */
  busy = false,
  onClose,
  onConfirm,
}) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');

  // Reset on open. (We deliberately don't memoize defaultName — the host
  // passes a fresh string each time so re-opening after a name change
  // surfaces the latest "<current> copy" suggestion.)
  useEffect(() => {
    if (!isOpen) return;
    setName(defaultName || '');
    setDescription('');
  }, [isOpen, defaultName]);

  const isNew = mode === 'new';
  const title = isNew ? 'Create new workflow' : 'Save current workflow as…';
  const confirmLabel = isNew ? 'Create workflow' : 'Save as new';
  const Icon = isNew ? Plus : CopyIcon;

  const canConfirm = name.trim().length > 0 && !busy;

  const handleConfirm = () => {
    if (!canConfirm) return;
    if (typeof onConfirm === 'function') {
      onConfirm({ name: name.trim(), description: description.trim() });
    }
  };

  return (
    <Modal
      isOpen={!!isOpen}
      onClose={busy ? null : onClose}
      dismissOnBackdrop={!busy}
      title={title}
      size="sm"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button
            variant="primary"
            onClick={handleConfirm}
            loading={busy}
            disabled={!canConfirm}
            iconLeft={<Icon size={12} />}
          >
            {confirmLabel}
          </Button>
        </>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12, fontSize: 12.5 }}>
        {isNew ? (
          <p style={{ margin: 0, color: '#475569', lineHeight: 1.5 }}>
            Creates a blank workflow seeded with a <code style={inlineCode}>Start → End</code>
            {' '}skeleton. You can begin editing immediately after creation.
          </p>
        ) : (
          <p style={{ margin: 0, color: '#475569', lineHeight: 1.5 }}>
            Clones the current workflow definition (nodes + connections) into a
            new workflow. The original is left untouched.
          </p>
        )}

        <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <span style={labelStyle}>
            Name <span style={{ color: '#dc2626' }}>*</span>
          </span>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') handleConfirm(); }}
            placeholder="e.g. Inbound alert triage"
            disabled={busy}
            autoFocus
            style={inputStyle}
          />
        </label>

        <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <span style={labelStyle}>Description</span>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Optional — what this workflow does, who runs it, what it touches"
            rows={3}
            disabled={busy}
            style={{ ...inputStyle, fontFamily: 'inherit' }}
          />
        </label>
      </div>
    </Modal>
  );
}

const labelStyle = {
  fontSize: 10.5, fontWeight: 600, color: '#0f172a',
  textTransform: 'uppercase', letterSpacing: 0.4,
};

const inputStyle = {
  width: '100%', padding: '6px 8px',
  fontSize: 12, color: '#0f172a',
  background: '#ffffff',
  border: '0.5px solid #cbd5e1', borderRadius: 4,
  outline: 'none', fontFamily: 'inherit',
};

const inlineCode = {
  background: '#f1f5f9', padding: '0 4px', borderRadius: 3,
  fontFamily: 'SFMono-Regular, Menlo, monospace', fontSize: 11,
};
